import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { STACK } from "./data";
import { Eyebrow, MaskLines, Reveal, ease } from "./motion";

const STATS = [
  { k: "1", v: "console for everything" },
  { k: "0", v: "per-device licence fees" },
  { k: "6", v: "systems, one platform" },
];

const Frame = ({ item }) => {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  return (
    <div onMouseMove={onMove} className="spotlight chamfer relative aspect-[4/5] sm:aspect-[5/4] w-full overflow-hidden bg-[#0B0F17]" data-testid="unifi-frame">
      <AnimatePresence mode="popLayout">
        <motion.img
          key={item.id}
          src={item.img}
          alt={item.name}
          className="absolute inset-0 h-full w-full object-cover"
                    initial={{ opacity: 0, scale: 1.15, clipPath: "inset(0 0 100% 0)" }}
          animate={{ opacity: 1, scale: 1, clipPath: "inset(0 0 0% 0)" }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease }}
        />
      </AnimatePresence>
      <div className="spotlight-mask absolute inset-0" />
      {["top-4 left-4", "top-4 right-4", "bottom-4 left-4", "bottom-4 right-4"].map((p) => (
        <span key={p} className={`absolute ${p} h-3 w-3 border-cyan-300/70 ${p.includes("top") ? "border-t" : "border-b"} ${p.includes("left") ? "border-l" : "border-r"}`} />
      ))}
      <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8 bg-gradient-to-t from-void via-void/70 to-transparent">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-300">{item.role}</p>
        <p className="mt-3 max-w-md text-slate-300 text-sm sm:text-base" data-testid="unifi-frame-caption">{item.body}</p>
      </div>
    </div>
  );
};

export const Stack = () => {
  const [active, setActive] = useState(STACK[0]);
  return (
    <section id="unifi" className="relative py-24 md:py-36 bg-slate-panel" data-testid="unifi-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Eyebrow>The Ubiquiti advantage</Eyebrow>
        <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1] max-w-4xl">
          <MaskLines lines={["One platform. One dashboard.", <span className="font-serif-i font-normal text-cyan-300">No licence fees.</span>]} />
        </h2>
        <div className="mt-10 sm:mt-16 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28 min-w-0">
            <Reveal><p className="text-slate-400 leading-relaxed max-w-md">Enterprise networking usually means enterprise pricing. UniFi changes that — gateways, switching, Wi-Fi, cameras and door entry, all from a single console with no recurring per-device licences. I design and deploy the full stack, then support it remotely.</p></Reveal>
            <div className="lg:hidden -mx-5 sm:-mx-8 mt-8 flex gap-2 overflow-x-auto px-5 sm:px-8 pb-2 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="UniFi products">
              {STACK.map((s, i) => {
                const on = s.id === active.id;
                return (
                  <button key={s.id} role="tab" aria-selected={on} onClick={() => setActive(s)} data-testid={`unifi-chip-${s.id}`} className={`snap-start shrink-0 flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm transition-colors ${on ? "border-cyan-300 bg-cyan-300 text-void font-semibold" : "border-white/15 text-slate-300"}`}>
                    <span className={`font-mono text-[10px] ${on ? "text-void/70" : "text-cyan-300/70"}`}>0{i + 1}</span>
                    {s.name}
                  </button>
                );
              })}
            </div>
            <ul className="hidden lg:block mt-10 border-t border-white/10" role="tablist">
              {STACK.map((s, i) => {
                const on = s.id === active.id;
                return (
                  <li key={s.id}>
                    <button role="tab" aria-selected={on} onMouseEnter={() => setActive(s)} onClick={() => setActive(s)} data-testid={`unifi-tab-${s.id}`} className="group w-full flex items-center justify-between py-4 border-b border-white/10 text-left">
                      <span className="flex items-baseline gap-4">
                        <span className={`font-mono text-[11px] transition-colors ${on ? "text-cyan-300" : "text-slate-600"}`}>0{i + 1}</span>
                        <span className={`font-display text-xl sm:text-2xl font-semibold transition-[color,transform] duration-500 ${on ? "text-white translate-x-2" : "text-slate-500 group-hover:text-slate-300"}`}>{s.name}</span>
                      </span>
                      <span className={`h-1.5 w-1.5 rounded-full transition-colors ${on ? "bg-cyan-300 shadow-[0_0_12px_#00F0FF]" : "bg-slate-700"}`} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="lg:col-span-7 -mt-4 lg:mt-0"><Frame item={active} /></div>
        </div>
        <div className="mt-14 sm:mt-20 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
          {STATS.map((s, i) => (
            <div key={s.v} className="bg-slate-panel">
              <Reveal delay={i * 0.1} className="p-4 sm:p-8">
                <p className="font-display font-black text-5xl sm:text-6xl text-white">{s.k}</p>
                <p className="mt-2 font-mono text-[9px] sm:text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.2em] text-slate-500 leading-relaxed">{s.v}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
